import { useCallback, useRef, type MutableRefObject } from 'react';
import { log } from '@/utils/logger';
import { MapBounds, MapOperationState } from '@/types/homepage';
import useHomePageStore from '@/store/useHomePageStore';

/**
 * Custom hook for map-related functionality in HomePage
 * Extracts map handlers to reduce HomePage complexity
 */
export function useHomePageMap(isSearchMoveRef: MutableRefObject<boolean>) {
  const {
    businesses,
    mapStatus,
    handleMapMove,
    setMapRef,
    updateMapStatus
  } = useHomePageStore();

  const logger = useRef(log.component('HomePageMap'));

  // Handle bounds change like TestMapPage - direct business loading
  const handleBoundsChange = useCallback(async (bounds: MapBounds) => {
    // Skip when the map moved as part of a search — handleFlyToComplete handles that load
    if (isSearchMoveRef.current) return;

    logger.current.debug('Bounds change detected', {
      bounds: {
        northEast: bounds.northEast,
        southWest: bounds.southWest
      },
      timestamp: new Date().toISOString(),
      currentBusinessCount: businesses.length,
      mapState: mapStatus.state
    });
    
    try {
      // Use handleMapMove for user drag/zoom operations (with deduplication and debouncing)
      await handleMapMove(bounds, false);
      
      logger.current.debug('Processed bounds change through handleMapMove', {
        boundsArea: {
          north: bounds.northEast.latitude,
          south: bounds.southWest.latitude, 
          east: bounds.northEast.longitude,
          west: bounds.southWest.longitude
        }
      });
    } catch (error) {
      logger.current.error('Error processing bounds change', error);
    }
  }, [handleMapMove, businesses.length, mapStatus.state]);

  // Store stable references for handleMapReady
  const updateMapStatusStable = useRef(updateMapStatus);
  const setMapRefStable = useRef(setMapRef);
  
  // Update refs when values change
  updateMapStatusStable.current = updateMapStatus;
  setMapRefStable.current = setMapRef;

  // Handle map ready - STABILIZED
  const handleMapReady = useCallback((
    mapInstance: maplibregl.Map,
    executeSearch: (query: string, directMapInstance?: maplibregl.Map, options?: { skipScroll?: boolean }) => Promise<void>,
    pendingSearchRef: React.MutableRefObject<string | null>,
    pendingSearchOptionsRef?: React.MutableRefObject<{ skipScroll?: boolean } | null>
  ) => {
    logger.current.info('Map ready handler called', {
      center: mapInstance.getCenter(),
      zoom: mapInstance.getZoom(),
      bounds: mapInstance.getBounds(),
      hasPendingSearch: !!pendingSearchRef.current,
      currentState: mapStatus.state
    });

    // Validate map instance
    if (!mapInstance) {
      logger.current.error('Invalid map instance in handleMapReady');
      return;
    }

    // Set map reference using the store action
    setMapRefStable.current(mapInstance);

    // Update map status to ready
    updateMapStatusStable.current({
      state: 'ready' as MapOperationState,
      initialLoadCompleted: true,
      lastLoadTime: Date.now(),
      userHasInteracted: false,
      businessesStable: false
    });

    // Execute pending search if exists
    if (pendingSearchRef.current) {
      logger.current.info('Executing pending search with direct map instance', { 
        searchQuery: pendingSearchRef.current,
        options: pendingSearchOptionsRef?.current
      });
      const searchQuery = pendingSearchRef.current;
      const searchOptions = pendingSearchOptionsRef?.current;
      pendingSearchRef.current = null;
      if (pendingSearchOptionsRef) {
        pendingSearchOptionsRef.current = null;
      }

      // Use requestAnimationFrame to ensure state updates are processed
      requestAnimationFrame(() => {
        executeSearch(searchQuery, mapInstance, searchOptions || undefined).catch(error => {
          logger.current.error('Error executing pending search', error);
          // Restore pending search if it fails
          pendingSearchRef.current = searchQuery;
          if (pendingSearchOptionsRef) {
            pendingSearchOptionsRef.current = searchOptions || null;
          }
        });
      });
    }
  }, [mapStatus.state]);

  return {
    handleBoundsChange,
    handleMapReady
  };
} 